import { useContext, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import classNames from "classnames";
import httpMethod from 'services/update2025/httpMethod';

import { Toast } from "primereact/toast";
import { InputText } from "primereact/inputtext";
import { Button } from "primereact/button";
import { Dialog } from "primereact/dialog";
import { Checkbox } from "primereact/checkbox";
import { ProgressSpinner } from "primereact/progressspinner";
import { Dropdown } from "primereact/dropdown";

import { ParameterContext } from "components/parameter/ParameterContext";
import { createParameter, updateParameter, validateParameter } from "services/ParameterService";
import { handleError } from "utils/handleError";
import { actionUpdate, actionView } from "utils/action";

export const ParameterEditor = () => {
    const toast = useRef(null);
    const { t, i18n } = useTranslation(["parameter", "common"]);
    const tokenObj = httpMethod.getTokenStorage(); // { access_token, … }
    const accessToken = tokenObj?.access_token;;

    let headers = {
        Authorization: `Bearer ${accessToken}`,
        "Accept-Language": i18n.language,
    };

    const parameterContext = useContext(ParameterContext);
    const { parameter, resetParameter, setParameter, parameters, setParameters, submitted, setSubmitted, parameterDialog, setParameterDialog } = parameterContext;
    const [showSpinner, setShowSpinner] = useState(false);
    const [validForm, setValidForm] = useState(false);
    const [titleEditor, setTitleEditor] = useState(t("parameter.titleCreateDetail"));

    useEffect(() => {
        // create
        if (!parameter.id) {
            setTitleEditor(t("parameter.titleCreateDetail"));
        } else {
            // update
            if (actionUpdate === parameter.action) {
                setTitleEditor(t("parameter.titleUpdateDetail"));
            }
            // view
            else {
                setTitleEditor(t("parameter.titleDetail"));
            }
        }
    });

    const calculationUnits = [
        { label: t("date"), value: "DATE" },
        { label: t("month"), value: "MONTH" },
        { label: t("year"), value: "YEAR" },
        { label: t("hour"), value: "HOUR" },
        { label: t("minute"), value: "MINUTE" },
        { label: t("second"), value: "SECOND" },
        { label: t("times"), value: "TIMES" },
    ];

    const onInputChange = (e, name) => {
        const val = e.target?.value || e.value || "";
        let _parameter = { ...parameter };
        _parameter[`${name}`] = val;

        setParameter(_parameter);
    };

    const onEnabledChange = (e) => {
        let _parameter = { ...parameter };
        _parameter["enabled"] = e.checked;
        setParameter(_parameter);
    };

    const hideDialog = () => {
        setSubmitted(false);
        setParameterDialog(false);
    };

    const saveParameterAction = () => {
        setValidForm(true);
        setShowSpinner(true);
        validateParameter(parameter)
            .then(() => {
                if (!parameter.id) {
                    // Creating
                    return createParameter(headers, parameter).then((res) => {
                        toast.current.show({
                            severity: "success",
                            summary: t("success", { ns: "common" }),
                            detail: t("inform.created", { ns: "common", object: t("parameter") }),
                            life: 3000,
                        });
                        setValidForm(false);
                        setSubmitted(true);
                        setParameterDialog(false);
                        resetParameter();
                    });
                } else {
                    // Updating
                    return updateParameter(headers, parameter.id, {
                        name: parameter.name,
                        value: parameter.value,
                        calculationUnit: parameter.calculationUnit,
                        enabled: parameter.enabled,
                    }).then((res) => {
                        toast.current.show({
                            severity: "success",
                            summary: t("success", { ns: "common" }),
                            detail: t("inform.updated", { ns: "common", object: t("parameter") }),
                            life: 3000,
                        });
                        setValidForm(false);
                        setSubmitted(true);
                        setParameterDialog(false);
                        resetParameter();
                    });
                }
            })
            .catch((error) => handleError(toast, t, error))
            .finally(() => setShowSpinner(false));
    };

    const findIndexById = (id) => {
        let index = -1;
        for (let i = 0; i < parameters.length; i++) {
            if (parameters[i].id === id) {
                index = i;
                break;
            }
        }

        return index;
    };

    const checkView = () => {
        return actionView === parameter?.action;
    };

    const dialogFooter = (
        <>
            <Button label={t("cancel", { ns: "common" })} icon="pi pi-times" className="p-button-text" onClick={hideDialog} />
            <Button label={t("save", { ns: "common" })} icon="pi pi-check" onClick={saveParameterAction} />
        </>
    );

    return (
        <>
            <Toast ref={toast} />

            <Dialog visible={parameterDialog} style={{ width: "50%" }} header={titleEditor.toUpperCase()} modal className="p-fluid text-center" onHide={hideDialog}>
                <div className="formgrid">
                    <div className="grid text-left">
                        <div className="col-12">
                            <div className="grid">
                                {/* code */}
                                <div className="field col-12 md:col-3">
                                    <label htmlFor="code">
                                        {t("code")}
                                        <span className="text-danger">*</span>
                                    </label>
                                    <InputText id="code" value={parameter?.code} keyfilter={/[^\s]/} maxLength={255} onChange={(e) => onInputChange(e, "code")} required autoFocus readOnly={parameter?.id} className={classNames({ "p-invalid": validForm && !parameter?.code })} />
                                    {validForm && !parameter?.code && <small className="p-invalid">{t("code.required")}.</small>}
                                </div>

                                {/* tên tham số */}
                                <div className="field col-12 md:col-9">
                                    <label htmlFor="name">
                                        {t("name")}
                                        <span className="text-danger">*</span>
                                    </label>
                                    <InputText id="name" value={parameter?.name} maxLength={255} onChange={(e) => onInputChange(e, "name")} required className={classNames({ "p-invalid": validForm && !parameter?.name })} readOnly={checkView()} />
                                    {validForm && !parameter?.name && <small className="p-invalid">{t("name.required")}.</small>}
                                </div>
                            </div>
                            <div className="grid">
                                {/* giá trị */}
                                <div className="field col-12 md:col-3">
                                    <label htmlFor="value">
                                        {t("value")}
                                        <span className="text-danger">*</span>
                                    </label>
                                    <InputText id="value" value={parameter?.value} maxLength={255} onChange={(e) => onInputChange(e, "value")} required className={classNames({ "p-invalid": validForm && !parameter?.value })} readOnly={checkView()} />
                                    {validForm && !parameter?.value && <small className="p-invalid">{t("value.required")}.</small>}
                                </div>

                                {/* đơn vị tính */}
                                <div className="field col-12 md:col-3">
                                    <label htmlFor="calculationUnit">
                                        {t("calculationUnit")}
                                        <span className="text-danger">*</span>
                                    </label>
                                    <Dropdown id="calculationUnit" required value={parameter?.calculationUnit} options={calculationUnits} onChange={(e) => onInputChange(e, "calculationUnit")} disabled={checkView()} className={classNames({ "p-invalid": validForm && !parameter?.value })} />
                                    {validForm && !parameter?.calculationUnit && <small className="p-invalid">{t("calculationUnit.required")}.</small>}
                                </div>

                                {/* hiệu lực */}
                                <div className="field col-12 md:col-6" style={{ display: "flex", justifyContent: "left", margin: "auto" }}>
                                    <label htmlFor="enabled">{t("enabled")}</label>
                                    <Checkbox id="enabled" checked={parameter?.enabled} onChange={onEnabledChange} readOnly={checkView()} />
                                </div>
                            </div>

                            {checkView() && (
                                <div className="grid">
                                    {/* người tạo */}
                                    <div className="field col-12 md:col-3">
                                        <label htmlFor="createdBy">{t("createdBy", { ns: "common" })}</label>
                                        <InputText id="createdBy" value={parameter?.createdBy?.username} readOnly />
                                    </div>
                                    {/* ngày tạo */}
                                    <div className="field col-12 md:col-3">
                                        <label htmlFor="createdDate">{t("createdDate", { ns: "common" })}</label>
                                        <InputText id="createdDate" value={parameter?.createdDate} readOnly />
                                    </div>
                                    {/* người sửa */}
                                    <div className="field col-12 md:col-3">
                                        <label htmlFor="modifiedBy">{t("modifiedBy", { ns: "common" })}</label>
                                        <InputText id="modifiedBy" value={parameter?.modifiedBy?.username} readOnly />
                                    </div>
                                    {/* ngày sửa */}
                                    <div className="field col-12 md:col-3">
                                        <label htmlFor="modifiedDate">{t("modifiedDate", { ns: "common" })}</label>
                                        <InputText id="modifiedDate" value={parameter?.modifiedDate} readOnly />
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
                <div className="grid">
                    <div className="col-12">
                        {/* save */}
                        {!checkView() && (
                            <Button
                                style={{
                                    width: "max-content",
                                }}
                                className="mr-2"
                                label={t("save", { ns: "common" })}
                                icon="pi pi-check"
                                onClick={saveParameterAction}
                            />
                        )}
                        {/* close */}
                        <Button
                            style={{
                                width: "max-content",
                            }}
                            className="mr-2 p-button-danger"
                            label={t("close", { ns: "common" })}
                            icon="pi pi-times"
                            onClick={hideDialog}
                        />
                    </div>
                </div>
            </Dialog>

            <Dialog visible={showSpinner} closable={false} resizable={false} draggable={false} showHeader={false}>
                <ProgressSpinner />
            </Dialog>
        </>
    );
};
